<template>
    <div class="mx-8">
        <AppPageHeader>
            <template #title>Procesos</template>
            <template #subtitle> Análisis y gestión de procesos </template>
            <template #headerRight>
                <AppButton
                    class="bg-gray-300"
                    :title="'Descargar información'"
                    @click="downloadInfo"
                >
                    <svg
                        class="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        ></path>
                    </svg>
                    Descargar
                </AppButton>
            </template>
        </AppPageHeader>

        <div class="flex gap-4 mb-8">
            <DashboardCardMini>
                <template #counter>{{ stats.total }}</template>
                <template #title>Total Empresas</template>
            </DashboardCardMini>

            <DashboardCardMini>
                <template #counter>{{ stats.completed }}</template>
                <template #title>Total Procesados</template>
            </DashboardCardMini>

            <DashboardCardMini>
                <template #counter>{{ stats.error }}</template>
                <template #title>Total Erróneos</template>
            </DashboardCardMini>
        </div>

        <div class="mb-12">
            <form
                @submit.prevent="handleUpload"
                class="flex flex-col gap-4 items-start"
            >
                <label class="font-medium text-gray-700"
                    >Subir archivo de procesos (CSV o Excel):</label
                >
                <p class="text-sm text-gray-500 -mt-2">
                    Nota: El archivo debe tener los nombres de las empresas en
                    la primera columna
                </p>
                <input
                    type="file"
                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    @change="onFileChange"
                    class="block border border-gray-300 rounded px-3 py-2 w-full max-w-md"
                />
                <AppButton
                    type="submit"
                    class="bg-blue-600 text-white"
                    :disabled="uploading"
                >
                    <Loader2
                        v-if="uploading"
                        class="w-5 h-5 text-white animate-spin mr-2"
                    />
                    <span v-else>Subir archivo</span>
                </AppButton>
                <div v-if="uploadError" class="text-red-600 mt-2">
                    {{ uploadError }}
                </div>
                <div v-if="uploadSuccess" class="text-green-600 mt-2">
                    Archivo subido correctamente
                </div>
            </form>
        </div>

        <!-- Listado de procesamiento por día -->
        <div class="mb-8">
            <h2 class="text-xl font-semibold text-gray-800 mb-4">
                Historial de Procesamiento
            </h2>
            <div
                v-if="loadingHistory"
                class="flex items-center justify-center py-8"
            >
                <Loader2 class="w-6 h-6 text-gray-400 animate-spin" />
            </div>
            <div
                v-else-if="processedHistory.length === 0"
                class="text-gray-500 py-4"
            >
                No hay registros procesados aún.
            </div>
            <div v-else class="bg-white rounded-lg shadow overflow-hidden">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                                Fecha
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                                Registros Procesados
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                                Registros Erróneos
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                                Acciones
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                        <tr
                            v-for="item in processedHistory"
                            :key="item.date"
                            class="hover:bg-gray-50"
                        >
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                            >
                                {{ item.formatted_date }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-green-600"
                            >
                                {{ item.completed_count }} registros procesados
                                exitosamente
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-red-600"
                            >
                                {{ item.error_count }} registros con error
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm">
                                <button
                                    v-if="item.error_count > 0"
                                    @click="downloadErrorsByDate(item.date)"
                                    class="text-blue-600 hover:text-blue-800 underline"
                                >
                                    Descargar errores
                                </button>
                                <span v-else class="text-gray-400">
                                    Sin errores
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
    <!-- Fin del template -->
</template>

<script setup lang="ts">
import axios from "axios";
import { ref, onMounted } from "vue";
import { Loader2 } from "lucide-vue-next";
import AppPageHeader from "../components/AppPageHeader.vue";
import AppButton from "../components/AppButton.vue";
import DashboardCardMini from "../components/DashboardCardMini.vue";

const uploading = ref(false);
const uploadError = ref("");
const uploadSuccess = ref(false);
const selectedFile = ref<File | null>(null);
const loadingHistory = ref(false);
const processedHistory = ref<
    Array<{
        date: string;
        formatted_date: string;
        completed_count: number;
        error_count: number;
    }>
>([]);

const stats = ref({
    total: 0,
    completed: 0,
    pending: 0,
    in_progress: 0,
    error: 0,
});

const fetchStats = async () => {
    try {
        const response = await axios.get("/v2/lead-data/stats");
        stats.value = response.data;
    } catch (error) {
        console.error("Error al cargar las estadísticas:", error);
    }
};

const fetchProcessedHistory = async () => {
    loadingHistory.value = true;
    try {
        const response = await axios.get("/v2/lead-data/processed-by-day");
        processedHistory.value = response.data;
    } catch (error) {
        console.error("Error al cargar el historial:", error);
    } finally {
        loadingHistory.value = false;
    }
};

onMounted(() => {
    fetchStats();
    fetchProcessedHistory();
});

const onFileChange = (event: Event) => {
    const files = (event.target as HTMLInputElement).files;
    if (files && files.length > 0) {
        selectedFile.value = files[0];
        uploadError.value = "";
        uploadSuccess.value = false;
    }
};

const handleUpload = async () => {
    if (!selectedFile.value) {
        uploadError.value = "Debes seleccionar un archivo.";
        return;
    }
    uploading.value = true;
    uploadError.value = "";
    uploadSuccess.value = false;
    try {
        const formData = new FormData();
        formData.append("file", selectedFile.value);

        const response = await axios.post("/v2/lead-data/upload", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

        if (response.data.success) {
            uploadSuccess.value = true;
            // Actualizar estadísticas y historial después de subir el archivo
            await fetchStats();
            await fetchProcessedHistory();
        } else {
            throw new Error(
                response.data.message || "Error al subir el archivo"
            );
        }
    } catch (error: any) {
        uploadError.value =
            error.response?.data?.message ||
            error.message ||
            "Error al subir el archivo. Inténtalo de nuevo.";
    } finally {
        uploading.value = false;
    }
};

const downloadInfo = async () => {
    try {
        const response = await axios.get("/v2/lead-data/download", {
            responseType: "blob",
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
            "download",
            `empresas_emails_${new Date().toISOString().slice(0, 10)}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Error al descargar el archivo:", error);
    }
};

const downloadErrorsByDate = async (date: string) => {
    try {
        const response = await axios.get(
            "/v2/lead-data/download-errors-by-date",
            {
                params: { date },
                responseType: "blob",
            }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `empresas_errores_${date}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Error al descargar el archivo:", error);
    }
};
</script>
